import Collection from 'ol/Collection';
import type Feature from 'ol/Feature';
import type { SimpleGeometry } from 'ol/geom';
import EventBus from '../EventBus';
import { MAP_EVENTS } from '../mapEvents';

/**
 * SelectionManager — shared store for the OpenLayers Collections the
 * map-layer Select and Modify interactions operate on.
 *
 * Split out of KmlLayer in step 4 of the layer split
 * (docs/superpowers/plans/2026-04-08-kml-layer-split.md) so that focused layers
 * (CentroidsLayer, PathLayer, future StaticPointsLayer / ZonesLayer)
 * can observe selection changes without owning a Collection of their
 * own — and so that KmlLayer stops being the sole "authority" on
 * selection state.
 *
 * The Collection references are STABLE for the lifetime of this
 * instance. Consumers mutate them via setSelected / setDraggable /
 * clearSelected / clearAll, which clear the existing Collection in
 * place and re-populate it, rather than creating new Collection
 * objects. This matters because:
 *
 *   1. OpenLayers Select / Modify interactions are bound to a specific
 *      Collection reference at construction time — replacing the
 *      reference would detach the interaction.
 *   2. Observers that subscribe to OL's `add` / `remove` events on the
 *      Collection would need to re-subscribe on every replacement.
 *
 * Observers have two options:
 *   A) Subscribe to `MAP_EVENTS.SELECTION_CHANGED` on the shared
 *      EventBus — fired once per mutation (set* / clear*). Cheapest
 *      and most convenient for layers that just need a "rebuild now"
 *      signal.
 *   B) Subscribe directly to the Collection's `add` and `remove`
 *      events for finer-grained notifications.
 */
export class SelectionManager {
  readonly selectedFeatures: Collection<Feature<SimpleGeometry>>;
  readonly draggableFeatures: Collection<Feature<SimpleGeometry>>;

  constructor() {
    this.selectedFeatures = new Collection<Feature<SimpleGeometry>>([]);
    this.draggableFeatures = new Collection<Feature<SimpleGeometry>>([]);
  }

  setSelected(features: Feature<SimpleGeometry>[]) {
    this.selectedFeatures.clear();

    for (const f of features) {
      this.selectedFeatures.push(f);
    }

    this.dispatchChange();
  }

  clearSelected() {
    if (this.selectedFeatures.getLength() === 0) {
      return;
    }

    this.selectedFeatures.clear();
    this.dispatchChange();
  }

  setDraggable(features: Feature<SimpleGeometry>[]) {
    this.draggableFeatures.clear();

    for (const f of features) {
      this.draggableFeatures.push(f);
    }
  }

  clearDraggable() {
    this.draggableFeatures.clear();
  }

  clearAll() {
    this.clearSelected();
    this.clearDraggable();
  }

  private dispatchChange() {
    EventBus.dispatch(MAP_EVENTS.SELECTION_CHANGED);
  }
}

/**
 * Default application-wide SelectionManager. KmlLayer and any future
 * selection-aware layers should import this singleton. Tests should
 * instantiate their own `new SelectionManager()` to avoid cross-test
 * contamination.
 */
const selectionManager = new SelectionManager();

export default selectionManager;
